import { ListOutput, PageableInput } from '@allocab/allocab-commons-shared-types';
import { VehicleOutput } from '@allocab/allocab-driver-service-types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { queryKeys } from '../services/datastore/queryKeys';
import { DEFAULT_CACHE_TIME, queryClient } from '../services/datastore/react-query';
import { InfiniteQueryOptions } from '../services/datastore/types';
import { driverApi } from '../services/network/Api';

export const VEHICLES_LIMIT = 10;

export type Input = Omit<PageableInput, 'offset' | 'limit'> & { limit?: number };

export const fetchVehiclesForDriver = (driverId: string, input: PageableInput) =>
  driverApi.get<PageableInput, VehicleOutput, 'list'>(`/v1/vehicle/vehicles/by-driver/${driverId}`, input);

export const useFethVehiclesForDriverPage = (driverId: string, input: Input, pageNumber = 1) => {
  const limit = input.limit ?? VEHICLES_LIMIT;
  const apiInput: PageableInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  return useQuery(
    queryKeys.user.fetchVehiclesForDriver(driverId, apiInput),
    () => fetchVehiclesForDriver(driverId, apiInput),
    {
      keepPreviousData: true,
    },
  );
};

export const prefetchVehiclesForDriverPage = (driverId: string, input: Input, pageNumber = 1) => {
  const limit = input.limit ?? VEHICLES_LIMIT;
  const apiInput: PageableInput = { ...input, limit, offset: (pageNumber - 1) * limit };

  queryClient.prefetchQuery(
    queryKeys.user.fetchVehiclesForDriver(driverId, apiInput),
    () => fetchVehiclesForDriver(driverId, apiInput),
    {
      staleTime: DEFAULT_CACHE_TIME,
    },
  );
};

export const useInfiniteFetchVehiclesForDriver = (
  driverId: string,
  input: Input,
  options: InfiniteQueryOptions<typeof queryKeys.user.fetchInfiniteVehiclesForDriver, ListOutput<VehicleOutput>> = {},
) => {
  const fetchVehiclesForDriverQuery = useInfiniteQuery(
    queryKeys.user.fetchInfiniteVehiclesForDriver(input),
    ({ pageParam = 0 }: { pageParam?: number }) =>
      fetchVehiclesForDriver(driverId, {
        limit: VEHICLES_LIMIT,
        offset: pageParam,
        orderBy: 'vehicle.current',
        ...input,
      }),
    {
      getNextPageParam: (lastPage, allPages) =>
        lastPage.nbItems < VEHICLES_LIMIT ? undefined : VEHICLES_LIMIT * allPages.length,
      ...options,
    },
  );

  return fetchVehiclesForDriverQuery;
};
